const STORAGE_KEY = 'openruns.participationDobIntent';
const INTENT_TTL_MS = 10 * 60 * 1000;

export const PARTICIPATION_INTENT_TYPES = Object.freeze({
  DIRECT_JOIN: 'direct_join',
  INVITE_ACCEPT: 'invite_accept',
  WAITLIST_JOIN: 'waitlist_join',
  SMS_JOIN: 'sms_join',
});

const ALLOWED_TYPES = new Set(Object.values(PARTICIPATION_INTENT_TYPES));
const SAFE_ID = '[A-Za-z0-9_-]+';
const ALLOWED_RETURN_PATHS = [
  new RegExp(`^/games/${SAFE_ID}$`),
  new RegExp(`^/join/${SAFE_ID}$`),
  new RegExp(`^/r/${SAFE_ID}$`),
  new RegExp(`^/run/${SAFE_ID}(?:\\?club=${SAFE_ID})?$`),
  /^\/notifications$/,
];

export const isInternalParticipationReturnPath = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return false;
  }
  return ALLOWED_RETURN_PATHS.some((pattern) => pattern.test(value));
};

const removeIntent = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
};

export const saveParticipationDobIntent = ({ type, returnTo }, now = Date.now()) => {
  if (!ALLOWED_TYPES.has(type) || !isInternalParticipationReturnPath(returnTo)) {
    return false;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      type,
      returnTo,
      expiresAt: now + INTENT_TTL_MS,
    }));
    return true;
  } catch {
    return false;
  }
};

export const readParticipationDobIntent = (now = Date.now()) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw);
    const valid = intent
      && ALLOWED_TYPES.has(intent.type)
      && isInternalParticipationReturnPath(intent.returnTo)
      && Number.isFinite(intent.expiresAt)
      && intent.expiresAt > now;
    if (!valid) {
      removeIntent();
      return null;
    }
    return intent;
  } catch {
    removeIntent();
    return null;
  }
};

export const consumeParticipationDobIntent = (now = Date.now()) => {
  const intent = readParticipationDobIntent(now);
  removeIntent();
  return intent;
};

export const isDobRequiredError = (error) => (
  error?.response?.status === 428
  && error?.response?.data?.detail?.code === 'DOB_REQUIRED'
);

export const persistParticipationDob = async ({ apiClient, dateOfBirth, beginUserUpdate }) => {
  const update = beginUserUpdate();
  await apiClient.patch('/users/profile', { date_of_birth: dateOfBirth });
  if (!update.isCurrent()) return null;
  const meResponse = await apiClient.get('/auth/me');
  if (!update.apply(meResponse.data)) return null;
  return meResponse.data;
};

export const getDobRetryMessage = (type) => {
  switch (type) {
    case PARTICIPATION_INTENT_TYPES.INVITE_ACCEPT:
      return 'Date of birth saved. Tap Join on the invitation again.';
    case PARTICIPATION_INTENT_TYPES.WAITLIST_JOIN:
      return 'Date of birth saved. Tap Join Waitlist again.';
    default:
      return 'Date of birth saved. Tap Join Run again.';
  }
};
